import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { eventBus } from '../eventBus';
import { log } from '../utils/DebugLogger';

// Side-effect imports for contract runtime registration
import '../contracts/LgpdTerms_ListInput';
import '../contracts/LgpdTerms_ListResponse';
import '../contracts/LgpdTerms_SaveInput';
import '../contracts/LgpdTerms_SaveResponse';
import '../contracts/LgpdTerms_ActivateInput';
import '../contracts/LgpdTerms_ActivateResponse';
import '../contracts/LgpdTerms_DeleteInput';
import '../contracts/LgpdTerms_DeleteResponse';
import '../contracts/LgpdTermAcceptances_ListInput';
import '../contracts/LgpdTermAcceptances_ListResponse';

// Type imports
import type { LgpdTerms_ListRow } from '../contracts/LgpdTerms_ListRow';
import type { LgpdTerms_SaveInput } from '../contracts/LgpdTerms_SaveInput';
import type { LgpdTermAcceptances_ListRow } from '../contracts/LgpdTermAcceptances_ListRow';

export function useLgpdAdmin() {
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  async function listTerms(activeOnly?: boolean): Promise<LgpdTerms_ListRow[]> {
    log('useLgpdAdmin', 'listTerms', 'Fetching terms list');
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post(
        '/index.php/lgpd_terms/index_api',
        { activeOnly: activeOnly ?? undefined },
        { inputContract: 'LgpdTerms_ListInput', responseContract: 'LgpdTerms_ListResponse' }
      );
      return response.dados || [];
    } catch (err) {
      log('useLgpdAdmin', 'listTerms', 'Error', err);
      const message = err instanceof Error ? err.message : 'Erro ao carregar termos LGPD';
      error.value = message;
      eventBus.emit('toast.show', { severity: 'error', summary: 'Erro', detail: message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function saveTerm(input: LgpdTerms_SaveInput): Promise<number> {
    log('useLgpdAdmin', 'saveTerm', 'Saving term', input);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post(
        '/index.php/lgpd_terms/save',
        input,
        { inputContract: 'LgpdTerms_SaveInput', responseContract: 'LgpdTerms_SaveResponse' }
      );
      const id = response.dados?.id;
      if (!id) throw new Error('ID não retornado ao salvar termo');
      return id;
    } catch (err) {
      log('useLgpdAdmin', 'saveTerm', 'Error', err);
      const message = err instanceof Error ? err.message : 'Erro ao salvar termo LGPD';
      error.value = message;
      eventBus.emit('toast.show', { severity: 'error', summary: 'Erro', detail: message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function activateTerm(id: number): Promise<void> {
    log('useLgpdAdmin', 'activateTerm', 'Activating term', id);
    isLoading.value = true;
    error.value = null;

    try {
      await httpService.post(
        '/index.php/lgpd_terms/activate',
        { id },
        { inputContract: 'LgpdTerms_ActivateInput', responseContract: 'LgpdTerms_ActivateResponse' }
      );
      eventBus.emit('lgpd_term.activated', { id });
    } catch (err) {
      log('useLgpdAdmin', 'activateTerm', 'Error', err);
      const message = err instanceof Error ? err.message : 'Erro ao ativar termo LGPD';
      error.value = message;
      eventBus.emit('toast.show', { severity: 'error', summary: 'Erro', detail: message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function deleteTerm(id: number): Promise<{ ok: true } | { ok: false; message: string }> {
    log('useLgpdAdmin', 'deleteTerm', 'Deleting term', id);
    isLoading.value = true;
    error.value = null;

    try {
      await httpService.post(
        '/index.php/lgpd_terms/delete',
        { id },
        { inputContract: 'LgpdTerms_DeleteInput', responseContract: 'LgpdTerms_DeleteResponse' }
      );
      return { ok: true };
    } catch (err) {
      log('useLgpdAdmin', 'deleteTerm', 'Error', err);
      const message = err instanceof Error ? err.message : 'Erro ao excluir termo LGPD';
      return { ok: false, message };
    } finally {
      isLoading.value = false;
    }
  }

  async function listAcceptances(
    filters: Partial<{
      term_id: number;
      cliente_id: number;
      franquia_id: number;
      data_inicio: string;
      data_fim: string;
      limit: number;
      offset: number;
    }>
  ): Promise<LgpdTermAcceptances_ListRow[]> {
    log('useLgpdAdmin', 'listAcceptances', 'Fetching acceptances', filters);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post(
        '/index.php/lgpd_term_acceptances/index_api',
        filters,
        { inputContract: 'LgpdTermAcceptances_ListInput', responseContract: 'LgpdTermAcceptances_ListResponse' }
      );
      return response.dados || [];
    } catch (err) {
      log('useLgpdAdmin', 'listAcceptances', 'Error', err);
      const message = err instanceof Error ? err.message : 'Erro ao carregar aceites';
      error.value = message;
      eventBus.emit('toast.show', { severity: 'error', summary: 'Erro', detail: message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function getAcceptanceDetail(id: number): Promise<LgpdTermAcceptances_ListRow | null> {
    log('useLgpdAdmin', 'getAcceptanceDetail', 'Fetching acceptance detail', id);
    try {
      const rows = await listAcceptances({});
      return rows.find((r) => r.id === id) || null;
    } catch (err) {
      log('useLgpdAdmin', 'getAcceptanceDetail', 'Error', err);
      return null;
    }
  }

  return {
    isLoading,
    error,
    listTerms,
    saveTerm,
    activateTerm,
    deleteTerm,
    listAcceptances,
    getAcceptanceDetail,
  };
}
